import { useState, useMemo, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight, Plus, X, Clock, MapPin, Users, Link2, Phone, Ticket, CheckCircle2, AlertCircle, Search, Filter, Settings, Copy, Trash2, Edit2, Bell } from 'lucide-react'
import { useCalendarStore } from '../store/calendarStore'
import { useMeetingsStore } from '../store/meetingsStore'

export default function CybertectionCalendarPage() {
  const calendarEvents = useCalendarStore((state) => state.events)
  const addEvent = useCalendarStore((state) => state.addEvent)
  const updateEvent = useCalendarStore((state) => state.updateEvent)
  const deleteEvent = useCalendarStore((state) => state.deleteEvent)
  const setSelectedDate = useCalendarStore((state) => state.setSelectedDate)
  const setViewMode = useCalendarStore((state) => state.setViewMode)
  const getMonthEvents = useCalendarStore((state) => state.getMonthEvents)
  const getWeekEvents = useCalendarStore((state) => state.getWeekEvents)
  const getEventsForDate = useCalendarStore((state) => state.getEventsForDate)
  const getCalendarStats = useCalendarStore((state) => state.getCalendarStats)
  const searchEvents = useCalendarStore((state) => state.searchEvents)

  const meetings = useMeetingsStore((state) => state.meetings)

  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [viewMode, setLocalViewMode] = useState('month')
  const [selectedDate, setLocalSelectedDate] = useState(new Date())
  const [showEventModal, setShowEventModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredType, setFilteredType] = useState('all')

  // Sync meetings with calendar
  useEffect(() => {
    meetings.forEach((meeting) => {
      const existingEvent = calendarEvents.find((e) => e.sourceId === meeting.id && e.type === 'meeting')
      if (!existingEvent) {
        // Add meeting to calendar if not already there
        addEvent({
          id: `MEET-${meeting.id}`,
          sourceId: meeting.id,
          type: 'meeting',
          title: meeting.title,
          description: meeting.description,
          startTime: meeting.startTime,
          endTime: meeting.endTime,
          duration: meeting.duration,
          location: meeting.location,
          attendees: meeting.attendees,
          conferenceLink: meeting.conferenceLink,
          organizer: meeting.organizer,
          color: 'blue',
          icon: '📹',
        })
      }
    })
  }, [meetings, calendarEvents, addEvent])

  const stats = useMemo(() => getCalendarStats(), [calendarEvents])

  const monthDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()

    const days = []
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), isCurrentMonth: false })
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true })
    }
    for (let i = 1; days.length < 42; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false })
    }

    return days
  }, [currentMonth])

  const monthEvents = useMemo(() => getMonthEvents(currentMonth), [currentMonth, calendarEvents])

  const todayEvents = useMemo(() => getEventsForDate(selectedDate), [selectedDate, calendarEvents])

  const filteredEvents = useMemo(() => {
    let events = searchQuery ? searchEvents(searchQuery) : calendarEvents
    if (filteredType !== 'all') {
      events = events.filter((e) => e.type === filteredType)
    }
    return events.sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
  }, [searchQuery, filteredType, calendarEvents, searchEvents])

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const handleDateClick = (date) => {
    setLocalSelectedDate(date)
  }

  const handleDeleteEvent = (eventId) => {
    if (window.confirm('Delete this event?')) {
      deleteEvent(eventId)
      setSelectedEvent(null)
    }
  }

  const getEventColor = (event) => {
    const colors = {
      blue: 'from-blue-600 to-blue-500',
      red: 'from-red-600 to-red-500',
      orange: 'from-orange-600 to-orange-500',
      yellow: 'from-yellow-600 to-yellow-500',
      green: 'from-green-600 to-green-500',
      purple: 'from-purple-600 to-purple-500',
    }
    return colors[event.color] || colors.blue
  }

  const getEventIcon = (event) => {
    const icons = {
      meeting: '📹',
      ticket: '🎫',
      call: '☎️',
      task: '✓',
      reminder: '🔔',
    }
    return icons[event.type] || '📌'
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Calendar className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Cybertection Calendar</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Unified Event Management Hub - Meetings, Tickets, Calls & More</p>
              </div>
              <div>
                <div className="text-4xl font-black text-yellow-300">{stats.upcoming}</div>
                <div className="text-sm text-blue-100">Upcoming Events</div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {[
            { label: 'Total Events', value: stats.total, icon: '📅', color: 'from-blue-600 to-blue-500' },
            { label: 'Upcoming', value: stats.upcoming, icon: '📌', color: 'from-cyan-600 to-cyan-500' },
            { label: 'Meetings', value: stats.meetings, icon: '📹', color: 'from-green-600 to-green-500' },
            { label: 'Tickets', value: stats.tickets, icon: '🎫', color: 'from-orange-600 to-orange-500' },
            { label: 'Calls', value: stats.calls, icon: '☎️', color: 'from-purple-600 to-purple-500' },
            { label: 'Overdue', value: stats.overdue, icon: '⚠️', color: 'from-red-600 to-red-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-4 hover:border-blue-400/40 transition-all shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-300 text-xs font-semibold">{stat.label}</p>
                  <p className="text-white text-3xl font-black mt-2">{stat.value}</p>
                </div>
                <div className="text-3xl">{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* Calendar View (Left - 3 cols) */}
          <div className="lg:col-span-3 space-y-6">

            {/* Calendar Navigation */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevMonth}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-blue-400" />
                  </button>
                  <button
                    onClick={() => setCurrentMonth(new Date())}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-blue-400" />
                  </button>
                </div>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-sm font-semibold text-blue-300 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {monthDays.map((dayObj, idx) => {
                  const dayEvents = monthEvents.filter(
                    (e) => new Date(e.startTime).toDateString() === dayObj.date.toDateString()
                  )
                  const isSelected = dayObj.date.toDateString() === selectedDate.toDateString()
                  const isToday = dayObj.date.toDateString() === new Date().toDateString()

                  return (
                    <div
                      key={idx}
                      onClick={() => handleDateClick(dayObj.date)}
                      className={`min-h-24 p-2 rounded-lg border transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-blue-600 border-blue-400'
                          : isToday
                          ? 'bg-slate-700 border-cyan-400'
                          : dayObj.isCurrentMonth
                          ? 'bg-slate-700/50 border-blue-500/20 hover:border-blue-400/40'
                          : 'bg-slate-800/30 border-slate-600/20'
                      }`}
                    >
                      <div className={`text-sm font-semibold mb-1 ${dayObj.isCurrentMonth ? 'text-white' : 'text-slate-500'}`}>
                        {dayObj.date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map((event) => (
                          <div
                            key={event.id}
                            className="text-xs bg-blue-500/30 text-blue-200 px-2 py-1 rounded truncate hover:bg-blue-500/50 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedEvent(event)
                            }}
                          >
                            {event.icon} {event.title.substring(0, 12)}...
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className="text-xs text-blue-300 px-2 py-1">
                            +{dayEvents.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Events for Selected Day */}
            {todayEvents.length > 0 && (
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
                <h3 className="text-xl font-bold text-white mb-4">
                  Events for {formatDate(selectedDate)}
                </h3>
                <div className="space-y-3">
                  {todayEvents.map((event) => (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20 hover:border-blue-400/40 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{getEventIcon(event)}</span>
                            <h4 className="font-semibold text-white">{event.title}</h4>
                          </div>
                          <p className="text-sm text-slate-300 mt-1">{formatTime(event.startTime)} - {formatTime(event.endTime || event.startTime)}</p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          event.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                          event.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                          'bg-blue-500/20 text-blue-300'
                        }`}>
                          {event.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar (Right - 1 col) */}
          <div className="space-y-6">

            {/* Search & Filter */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Search Events</label>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-blue-500/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Filter by Type</label>
                  <select
                    value={filteredType}
                    onChange={(e) => setFilteredType(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-blue-500/20 rounded-lg text-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="all">All Events</option>
                    <option value="meeting">Meetings</option>
                    <option value="ticket">Tickets</option>
                    <option value="call">Calls</option>
                    <option value="task">Tasks</option>
                    <option value="reminder">Reminders</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Upcoming
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredEvents.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className="bg-slate-700/50 rounded-lg p-3 border border-blue-500/20 cursor-pointer hover:border-blue-400/40 transition-all"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg mt-1">{getEventIcon(event)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{event.title}</p>
                        <p className="text-xs text-slate-400">{formatDate(event.startTime)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Event Type Legend */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
              <h3 className="text-lg font-bold text-white mb-4">Event Types</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><span className="text-lg">📹</span><span className="text-slate-300">Meetings</span></div>
                <div className="flex items-center gap-2"><span className="text-lg">🎫</span><span className="text-slate-300">Tickets</span></div>
                <div className="flex items-center gap-2"><span className="text-lg">☎️</span><span className="text-slate-300">Calls</span></div>
                <div className="flex items-center gap-2"><span className="text-lg">✓</span><span className="text-slate-300">Tasks</span></div>
                <div className="flex items-center gap-2"><span className="text-lg">🔔</span><span className="text-slate-300">Reminders</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{getEventIcon(selectedEvent)}</span>
                <h2 className="text-xl font-semibold text-white">{selectedEvent.title}</h2>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Status */}
              <div className={`px-4 py-2 rounded-full text-center font-semibold ${
                selectedEvent.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                selectedEvent.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                'bg-blue-500/20 text-blue-300'
              }`}>
                {selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
              </div>

              {/* Date & Time */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-300">Date & Time</h3>
                <p className="text-white">{formatDate(selectedEvent.startTime)}</p>
                <p className="text-slate-300 text-sm">{formatTime(selectedEvent.startTime)} - {formatTime(selectedEvent.endTime || selectedEvent.startTime)}</p>
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-slate-300">Description</h3>
                  <p className="text-slate-300">{selectedEvent.description}</p>
                </div>
              )}

              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-400 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-slate-300">Location</h3>
                    <p className="text-white">{selectedEvent.location}</p>
                  </div>
                </div>
              )}

              {/* Conference Link */}
              {selectedEvent.conferenceLink && (
                <div className="flex items-start gap-2">
                  <Link2 className="w-4 h-4 text-blue-400 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-slate-300">Conference Link</h3>
                    <a href={selectedEvent.conferenceLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 break-all">
                      {selectedEvent.conferenceLink}
                    </a>
                  </div>
                </div>
              )}

              {/* Attendees */}
              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Attendees
                  </h3>
                  <div className="space-y-1">
                    {selectedEvent.attendees.map((attendee) => (
                      <div key={attendee.id} className="flex items-center justify-between text-sm">
                        <span className="text-white">{attendee.name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          attendee.status === 'accepted' ? 'bg-green-500/20 text-green-300' :
                          attendee.status === 'declined' ? 'bg-red-500/20 text-red-300' :
                          'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {attendee.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 border-t border-slate-700 p-6">
              <button
                onClick={() => setSelectedEvent(null)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => handleDeleteEvent(selectedEvent.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
